import { Span, Transaction } from '@sentry/tracing';
import { EXTENSIONS } from './constants';
import { events } from './events';
import { Module } from './framework/module';
import { PlEvent } from './models/pl.event';
import { toastWarn } from './toasts';
import { ConcurrencyOption } from './types/concurrency-option.type';
import { QualityOption } from './types/quality-option.type';
import { createTransaction } from './utils/create-transaction.fn';
import { getBuildNumber } from './utils/get-build-number.fn';
import { isProd } from './utils/is-prod.fn';
import { nextFrame } from './utils/next-frame.fn';
import { partition } from './utils/partition.fn';
import { range } from './utils/range.fn';
import { readFile } from './utils/read-file.fn';
import { timerSync } from './utils/timer-sync.fn';
import { timer } from './utils/timer.fn';
import { toBlob } from './utils/to-blob.fn';

export class ConvertModule extends Module {
  async onInit() {
    events.on('convert', (event) => this.onConvert(event));
    events.on('convert-finished-without-errors', (event) =>
      this.onFinishedWithoutErrors(event)
    );
    events.on('convert-finished-with-errors', (event) =>
      this.onFinishedWithErrors(event)
    );
  }

  private createWorker(span?: Span) {
    const s = span?.startChild({ op: 'createWorker' });
    const buildNumber = getBuildNumber();
    const worker = new Worker(`/js/app/worker.app.js?b=${buildNumber}`, {
      name: 'heic_converter_worker',
    });

    s?.finish();

    return worker;
  }

  private async renderInWorker(
    worker: Worker,
    arrayBuffer: ArrayBuffer,
    file: File,
    span: Span | undefined
  ) {
    const s = span?.startChild({
      op: 'renderInWorker',
    });
    const workerTask = new Promise<{
      displayData: Omit<ImageData, 'colorSpace'> | undefined;
      error?: any;
    }>((resolve) => {
      worker.onmessage = (e) => resolve(e.data);
    });

    const workerMessage = {
      arrayBuffer,
      file: { name: file.name, size: file.size },
      b: getBuildNumber(),
      isProd: isProd(),
    };

    worker.postMessage(workerMessage);

    const workerResult = await workerTask;

    if (workerResult?.displayData === undefined) {
      s?.setData('internalError', 'error while rendering in worker')
        .setData('workerError', JSON.stringify(workerResult?.error))
        ?.setStatus('aborted')
        ?.finish();

      return workerResult;
    }

    s?.finish();

    return workerResult;
  }

  private async render(
    worker: Worker,
    arrayBuffer: ArrayBuffer,
    file: File,

    span: Span | undefined
  ) {
    const s = span?.startChild({
      op: 'render',
      data: { file: { name: file.name, size: file.size } },
    });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (context === null) {
      s?.setData('internalError', 'unable to create canvas for rendering')
        .setStatus('aborted')
        ?.finish();

      return;
    }

    const workerResult = await timer('renderInWorker', () =>
      this.renderInWorker(worker, arrayBuffer, file, span)
    );

    if (workerResult?.error === 'not_heif') {
      events.emit('info', { message: `"${file.name}" is not a HEIC image` });

      s?.setData('internalError', `"${file.name}" is not a HEIC image`)
        ?.setStatus('aborted')
        ?.finish();

      return;
    }

    if (workerResult?.displayData === undefined) return;

    const width = workerResult.displayData.width;
    const height = workerResult.displayData.height;

    canvas.width = width;
    canvas.height = height;

    let contextBuffer: ImageData;

    try {
      contextBuffer = context.createImageData(width, height);
    } catch (error) {
      if (error instanceof RangeError) {
        events.emit('warn', {
          message: `"${file.name}" was too big, we ran out of memory.`,
        });

        s?.setData(
          'internalError',
          `"${file.name}" was too big, ran out of memory`
        )
          ?.setData('width', width)
          ?.setData('height', height)
          ?.setStatus('aborted')
          ?.finish();
        return;
      }

      throw error;
    }

    await timerSync('fill buffer', async () => {
      await nextFrame(() => {
        if (workerResult?.displayData?.data === undefined) {
          throw new Error(
            'unable to fill buffer, apparantly no data in array buffer'
          );
        }

        for (let i = 0; i < workerResult.displayData.data.length; i++) {
          contextBuffer.data[i] = workerResult.displayData.data[i];
        }
      });
    });

    await nextFrame(() =>
      timerSync('fill canvas', () => context?.putImageData(contextBuffer, 0, 0))
    );

    s?.finish();

    return canvas;
  }

  private async renderImage(
    worker: Worker,

    file: File,
    quality: QualityOption,
    span: Span | undefined
  ) {
    const s = span?.startChild({
      op: 'renderImage',
      data: { file: { name: file.name, size: file.size } },
    });
    const filename = file.name
      .split('.')
      .slice(0, -1)
      .join('.')
      .concat(`.${EXTENSIONS[quality.value.mime]}`);

    events.emit('progress-info', {
      text: `Converting ${file.name} → ${filename}`,
    });

    const start = performance.now();
    const arrayBuffer = await timer('readFile', () => readFile(file, s));

    if (arrayBuffer === null || typeof arrayBuffer === 'string') {
      s?.setData('internalError', 'not an array buffer')
        ?.setStatus('aborted')
        ?.finish();

      return;
    }

    const canvas = await this.render(worker, arrayBuffer, file, s);

    if (canvas === undefined) return;

    const blob = await toBlob(
      canvas,
      quality.value.mime,
      quality.value.quality,
      s
    );
    const end = performance.now();
    const elapsed = end - start;

    events.emit('progress-info', {
      text: `Finished ${filename}`,
    });

    s?.finish();

    return { blob, filename, elapsed };
  }

  private calculateNextEta(
    previousElapsed: number[],
    finished: number,
    total: number
  ): number {
    const averageElapsed =
      previousElapsed.reduce((sum, e) => sum + e, 0) / finished / 1000;
    const remaining = total - finished;
    const newEta = remaining * averageElapsed;

    return newEta;
  }

  private async handleMultipleFiles(
    files: FileList,
    quality: QualityOption,
    concurrency: ConcurrencyOption,
    transaction: Transaction | null
  ) {
    const numberOfWorkers =
      concurrency.value > files.length ? files.length : concurrency.value;
    const s = transaction?.startChild({
      op: 'handleMultipleFiles',
      data: {
        numberOfWorkers,
      },
    });
    const elapsedResults: number[] = [];
    let finished = 1;

    const workers = [...range(0, numberOfWorkers - 1)].map(() =>
      this.createWorker(s)
    );
    const partitioned = partition([...files], workers.length);
    let filesNotConverted: File[] = [];

    for (const partitionedResult of partitioned) {
      const partitionTasks = partitionedResult.map(async (file, index) => {
        const result = await this.renderImage(workers[index], file, quality, s);

        if (result) {
          const { blob, filename, elapsed: elapsedRendering } = result;

          if (blob) {
            const s1 = s?.startChild({
              op: 'add-to-zip',
            });
            const wasAdded = events.onceIf(
              'added-to-zip',
              ({ filename: f }) => f === filename
            );

            events.emit('add-to-zip', { blob, filename });

            const { elapsed: elapsedAddingZip } = await wasAdded;
            s1?.finish();

            const totalElapsed = elapsedRendering + elapsedAddingZip;
            elapsedResults.push(totalElapsed);

            const eta = this.calculateNextEta(
              elapsedResults,
              finished,
              files.length
            );
            events.emit('progress-eta', { seconds: eta });

            finished++;

            events.emit('progress', {
              percent: Math.round((finished / files.length) * 100),
            });
          }
        } else {
          events.emit('progress-info', {
            text: `Failed to convert ${file.name}`,
          });

          filesNotConverted.push(file);
        }
      });

      await Promise.all(partitionTasks);

      const eta = this.calculateNextEta(elapsedResults, finished, files.length);
      events.emit('progress-eta', { seconds: eta });
    }

    if (filesNotConverted.length > 0) {
      toastWarn('Some file(s) were not converted because of an unknown error');
      events.emit('convert-finished-with-errors', {
        files,
        numberOfFailedFiles: filesNotConverted.length,
      });
    }

    if (filesNotConverted.length === files.length) {
      s?.setData('internalError', 'no files were converted')
        ?.setStatus('aborted')
        ?.finish();
      events.emit('progress-close');

      return;
    }

    workers.forEach((w) => w.terminate());

    const zipWasGenerated = events.onceAsync('generated-zip');

    events.emit('generate-zip');

    s?.finish();

    await zipWasGenerated;

    events.emit('convert-finished');
    events.emit('download-zip');
    events.emit('convert-finished-without-errors', { files });
  }

  private async handleSingleFile(
    file: File,
    quality: QualityOption,
    transaction: Transaction | null
  ) {
    const s = transaction?.startChild({
      op: 'handleSingleFile',
      data: {
        filename: file.name,
        quality,
      },
    });
    const worker = this.createWorker(s);
    const result = await this.renderImage(worker, file, quality, s);

    if (result) {
      const { blob, filename } = result;

      if (blob) {
        events.emit('convert-finished');
        events.emit('download', { blob, filename });

        events.emit('convert-finished-without-errors', { files: [file] });
      } else {
        events.emit('progress-info', {
          text: `Failed to convert ${file.name}`,
        });
        events.emit('progress-close');

        toastWarn(`Unable to convert ${file.name}, an unknown error occurred.`);

        events.emit('convert-finished-with-errors', {
          files: [file],
          numberOfFailedFiles: 1,
        });
      }
    } else {
      events.emit('progress-info', {
        text: `Failed to convert ${file.name}`,
      });
      events.emit('progress-close');

      toastWarn(`Unable to convert ${file.name}, an unknown error occurred.`);

      events.emit('convert-finished-with-errors', {
        files: [file],
        numberOfFailedFiles: 1,
      });
    }

    s?.finish();

    worker.terminate();
  }

  private async onConvert({
    files,
    quality,
    concurrency,
  }: {
    files: FileList;
    quality: QualityOption;
    concurrency: ConcurrencyOption;
  }) {
    try {
      const t = createTransaction('Convert Files', 'convert', {
        numberOfFiles: files.length,
        quality,
        concurrency,
        files: [...files].map(
          ({ name, size, type, lastModified, webkitRelativePath }) => ({
            name,
            size,
            type,
            lastModified,
            webkitRelativePath,
          })
        ),
      });

      if (files.length === 1) {
        await this.handleSingleFile(files[0], quality, t);
      } else {
        await timer('convert', () =>
          this.handleMultipleFiles(files, quality, concurrency, t)
        );
      }

      t?.finish();
    } catch (errorPayload) {
      events.emit('error', {
        error: new Error('Error while converting'),
        errorPayload,
      });
    }
  }

  private onFinishedWithoutErrors({ files }: { files: FileList | File[] }) {
    document.dispatchEvent(
      new PlEvent({
        goal: 'converted files',
        props: {
          numberOfFiles: files.length,
        },
      })
    );
  }

  private onFinishedWithErrors({
    numberOfFailedFiles,
    files,
  }: {
    numberOfFailedFiles: number;
    files: FileList | File[];
  }) {
    document.dispatchEvent(
      new PlEvent({
        goal: 'converted with errors',
        props: {
          numberOfFiles: files.length,
          numberOfFailedFiles,
          failureRate:
            files.length === 0
              ? '1'
              : ((numberOfFailedFiles / files.length) * 100).toFixed(2),
        },
      })
    );
  }
}
